import { useState, type ReactNode } from 'react';
import {
  PiArrowSquareOut,
  PiCheckBold,
  PiCheckLight,
  PiCopyLight,
  PiDotsThreeVertical,
  PiPencilSimpleLineLight,
  PiXLight,
} from 'react-icons/pi';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../../atoms/buttons/button';
import Label from '../../../atoms/inputs/label';
import Loading from '../../../atoms/loading';
import Tooltip from '../../../atoms/tooltips/tooltip';
import { Text } from '../../../atoms/text';
import { getTextSize } from '../../../atoms/text/text.styles';
import Dropdown, {
  type DropdownItem,
} from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { useCopyToClipboard } from 'src/modules/shared/hooks/copy-to-clipboard';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  getInlineButtonSize,
  getInlineIconSize,
} from '../../../atoms/buttons/button/button.styles';
import {
  ensureUrlWithProtocol,
  isProbablyUrl,
} from 'src/modules/shared/utils/validation/url.util';
import {
  stackedFieldButtonClassName,
  stackedFieldValueClassName,
} from 'src/modules/shared/styles/stacked-field.styles';
import {
  type ComponentAppearance,
  type ComponentSize,
  type ComponentVariant,
} from 'src/modules/shared/types/component.type';

interface InlineEditableInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

export interface InlineEditableInputWrapperProps {
  label: string;
  value: string;
  onSave: (value: string) => Promise<void>;
  loading?: boolean;
  size?: ComponentSize;
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
  disabled?: boolean;
  disableCopy?: boolean;
  hideLabel?: boolean;
  onCancel?: () => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  formatDisplayValue?: (value: string) => string;
  children: (props: InlineEditableInputProps) => ReactNode;
  buttonLabelClassName?: string;
  alignment?: 'left' | 'right';
  layout?: 'inline' | 'stacked';
}

const getCopyableValue = (value: string) => (value ? value : undefined);

const getUtilityItems = ({
  disableCopy,
  isCopied,
  t,
  value,
  copyToClipboard,
}: {
  disableCopy: boolean;
  isCopied: boolean;
  t: ReturnType<typeof useI18n>['t'];
  value: string;
  copyToClipboard: (value?: string) => Promise<boolean>;
}) =>
  [
    !disableCopy && getCopyableValue(value)
      ? {
          content: isCopied
            ? t('shared.inline.copied')
            : t('shared.inline.copy'),
          icon: isCopied ? (
            <PiCheckBold className="h-4 w-4 text-success-500" />
          ) : (
            <PiCopyLight className="h-4 w-4" />
          ),
          onClick: () => copyToClipboard(getCopyableValue(value)),
        }
      : null,
    value && isProbablyUrl(value)
      ? {
          content: t('shared.inline.openLink'),
          icon: <PiArrowSquareOut className="h-4 w-4" />,
          onClick: () =>
            window.open(ensureUrlWithProtocol(value), '_blank', 'noopener'),
        }
      : null,
  ].filter(Boolean) as DropdownItem[];

const InlineEditableUtilityActions = ({
  copyToClipboard,
  disableCopy,
  disabled,
  isCopied,
  isStacked,
  label,
  size,
  t,
  utilityItems,
  value,
}: {
  copyToClipboard: (value?: string) => Promise<boolean>;
  disableCopy: boolean;
  disabled: boolean;
  isCopied: boolean;
  isStacked: boolean;
  label: string;
  size: ComponentSize;
  t: ReturnType<typeof useI18n>['t'];
  utilityItems: DropdownItem[];
  value: string;
}) => {
  if (utilityItems.length === 0) return null;

  const iconSize = getInlineIconSize(size);
  const containerClassName = isStacked
    ? 'flex shrink-0 items-center gap-1 self-center'
    : 'flex items-center gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100';

  if (isStacked) {
    return (
      <div className={containerClassName}>
        <Dropdown
          items={utilityItems}
          placement="bottom-end"
          mobileTitle={label}
          handler={
            <Button
              variant="neutral"
              appearance="ghost"
              size={size}
              isIconOnly
              disabled={disabled}
            >
              <PiDotsThreeVertical className={iconSize} />
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {getCopyableValue(value) && !disableCopy && (
        <Tooltip
          content={
            isCopied ? t('shared.inline.copied') : t('shared.inline.copy')
          }
          placement="top"
          size="sm"
        >
          <Button
            variant="neutral"
            appearance="ghost"
            size={size}
            onClick={() => copyToClipboard(getCopyableValue(value))}
            isIconOnly
            disabled={disabled}
          >
            {isCopied ? (
              <PiCheckBold className={`${iconSize} text-success-500`} />
            ) : (
              <PiCopyLight className={iconSize} />
            )}
          </Button>
        </Tooltip>
      )}
      {value && isProbablyUrl(value) && (
        <Tooltip
          content={t('shared.inline.openLink')}
          placement="top"
          size="sm"
        >
          <Button
            variant="neutral"
            appearance="ghost"
            size={size}
            onClick={() =>
              window.open(ensureUrlWithProtocol(value), '_blank', 'noopener')
            }
            isIconOnly
            disabled={disabled}
          >
            <PiArrowSquareOut className={iconSize} />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

const InlineEditableDisplayButton = ({
  buttonLabelClasses,
  disabled,
  displayValue,
  isStacked,
  onEdit,
  size,
  value,
  variant,
  appearance,
}: {
  buttonLabelClasses: string;
  disabled: boolean;
  displayValue: string;
  isStacked: boolean;
  onEdit: () => void;
  size: ComponentSize;
  value: string;
  variant: ComponentVariant;
  appearance: ComponentAppearance;
}) => {
  const tooltipContent = disabled
    ? undefined
    : value && displayValue.length > 30
      ? displayValue
      : undefined;

  return (
    <Tooltip
      content={tooltipContent}
      placement="top"
      size="sm"
      contentClassName={isStacked ? 'w-full flex-1' : ''}
    >
      <Button
        variant={variant}
        appearance={appearance}
        size={size}
        onClick={disabled ? undefined : onEdit}
        disabled={disabled}
        className={`${buttonLabelClasses} ${
          isStacked
            ? `stacked-field-control !min-w-0 !max-w-none ${stackedFieldButtonClassName}`
            : ''
        }`}
      >
        <span
          className={`min-w-0 flex-1 truncate text-left ${
            isStacked ? stackedFieldValueClassName.replace(/!/g, '') : ''
          }`}
        >
          <Text
            size={getTextSize(size)}
            className={`truncate ${!value ? 'text-secondary-500 ' : ''}${
              isStacked ? stackedFieldValueClassName : ''
            }`}
          >
            {value ? displayValue : '—'}
          </Text>
        </span>
        {isStacked && (
          <PiPencilSimpleLineLight className="h-4 w-4 flex-shrink-0 text-secondary-500" />
        )}
      </Button>
    </Tooltip>
  );
};

const InlineEditableEditState = ({
  buttonSize,
  children,
  disabled,
  editedValue,
  handleCancel,
  handleSave,
  loading,
  setEditedValue,
  variant,
}: {
  buttonSize: ComponentSize;
  children: (props: InlineEditableInputProps) => ReactNode;
  disabled: boolean;
  editedValue: string;
  handleCancel: () => void;
  handleSave: () => Promise<void>;
  loading: boolean;
  setEditedValue: (value: string) => void;
  variant: ComponentVariant;
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="flex-1 w-full"
  >
    <div className="relative rounded-lg bg-gray-50">
      {children({
        value: editedValue,
        onChange: setEditedValue,
        disabled: loading || disabled,
      })}
      <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
        <Button
          variant="neutral"
          appearance="ghost"
          size={buttonSize}
          onClick={handleCancel}
          isIconOnly
          disabled={loading}
        >
          <PiXLight className={getInlineIconSize(buttonSize)} />
        </Button>
        <Button
          variant={variant}
          size={buttonSize}
          onClick={() => {
            void handleSave();
          }}
          disabled={loading}
          isIconOnly
        >
          {loading ? (
            <Loading size="2xs" variant="primary" appearance="outline" />
          ) : (
            <PiCheckLight className={getInlineIconSize(buttonSize)} />
          )}
        </Button>
      </div>
    </div>
  </motion.div>
);

const InlineEditableDisplayState = ({
  alignmentClasses,
  buttonLabelClasses,
  copyToClipboard,
  disableCopy,
  disabled,
  displayValue,
  hideLabel,
  isCopied,
  isStacked,
  label,
  onEdit,
  size,
  t,
  utilityItems,
  value,
  variant,
  appearance,
}: {
  alignmentClasses: string;
  buttonLabelClasses: string;
  copyToClipboard: (value?: string) => Promise<boolean>;
  disableCopy: boolean;
  disabled: boolean;
  displayValue: string;
  hideLabel: boolean;
  isCopied: boolean;
  isStacked: boolean;
  label: string;
  onEdit: () => void;
  size: ComponentSize;
  t: ReturnType<typeof useI18n>['t'];
  utilityItems: DropdownItem[];
  value: string;
  variant: ComponentVariant;
  appearance: ComponentAppearance;
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className={`flex-1 w-full ${
      isStacked
        ? 'flex flex-col items-stretch gap-1'
        : 'flex items-center justify-between px-2'
    }`}
  >
    {!isStacked && !hideLabel && (
      <Label
        size={size}
        variant="primary"
        className="max-w-[40%] flex-shrink-0 break-words font-light"
      >
        {label}
      </Label>
    )}
    <div
      className={`flex-1 ${
        isStacked
          ? 'w-full'
          : `flex items-center ${hideLabel ? 'min-w-0' : 'min-w-[40%]'} ${alignmentClasses}`
      }`}
    >
      <div
        className={`flex w-full min-w-0 ${
          isStacked ? 'items-center gap-2' : 'items-center'
        }`}
      >
        <div
          className={`flex min-w-0 ${
            isStacked ? 'flex-1 justify-stretch' : ''
          }`}
        >
          <InlineEditableDisplayButton
            buttonLabelClasses={buttonLabelClasses}
            disabled={disabled}
            displayValue={displayValue}
            isStacked={isStacked}
            onEdit={onEdit}
            size={size}
            value={value}
            variant={variant}
            appearance={appearance}
          />
        </div>
        <InlineEditableUtilityActions
          copyToClipboard={copyToClipboard}
          disableCopy={disableCopy}
          disabled={disabled}
          isCopied={isCopied}
          isStacked={isStacked}
          label={label}
          size={size}
          t={t}
          utilityItems={utilityItems}
          value={value}
        />
      </div>
    </div>
  </motion.div>
);

export const InlineEditableInputWrapper: React.FC<
  InlineEditableInputWrapperProps
> = ({
  label,
  value,
  onSave,
  loading = false,
  size = 'sm',
  variant = 'primary',
  appearance = 'ghost',
  disabled = false,
  disableCopy = false,
  onCancel,
  onError,
  onSuccess,
  formatDisplayValue = (currentValue) => currentValue,
  children,
  buttonLabelClassName,
  alignment = 'right',
  hideLabel = false,
  layout = 'inline',
}) => {
  const { t } = useI18n();
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  const isStacked = layout === 'stacked';
  const buttonSize = getInlineButtonSize(size);
  const utilityItems = getUtilityItems({
    disableCopy,
    isCopied,
    t,
    value,
    copyToClipboard,
  });
  const displayValue = formatDisplayValue(value);
  const alignmentClasses =
    alignment === 'left'
      ? 'text-left !justify-start'
      : 'text-right !justify-end';
  const buttonLabelClasses = `font-light group-hover:bg-gray-100 px-2 py-1 rounded transition-all relative flex items-center gap-2 min-w-[100px] max-w-3/4 ${alignmentClasses} ${buttonLabelClassName} ${disabled ? 'opacity-70' : 'hover:text-primary-600'}`;

  const handleSave = async () => {
    try {
      await onSave(editedValue);
      onSuccess?.();
      setIsEditing(false);
    } catch (error) {
      onError?.(error as Error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedValue(value);
    onCancel?.();
  };

  return (
    <div className="group relative">
      <div
        className={
          isStacked ? 'flex w-full flex-col gap-0.5' : 'flex items-center'
        }
      >
        {isStacked && !hideLabel && (
          <Label
            size="xs"
            variant="secondary"
            layout="stacked"
            className="break-words"
          >
            {label}
          </Label>
        )}
        <AnimatePresence>
          {isEditing ? (
            <InlineEditableEditState
              buttonSize={buttonSize}
              disabled={disabled}
              editedValue={editedValue}
              handleCancel={handleCancel}
              handleSave={handleSave}
              loading={loading}
              setEditedValue={setEditedValue}
              variant={variant}
            >
              {children}
            </InlineEditableEditState>
          ) : (
            <InlineEditableDisplayState
              alignmentClasses={alignmentClasses}
              buttonLabelClasses={buttonLabelClasses}
              copyToClipboard={copyToClipboard}
              disableCopy={disableCopy}
              disabled={disabled}
              displayValue={displayValue}
              hideLabel={hideLabel}
              isCopied={isCopied}
              isStacked={isStacked}
              label={label}
              onEdit={() => setIsEditing(true)}
              size={size}
              t={t}
              utilityItems={utilityItems}
              value={value}
              variant={variant}
              appearance={appearance}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
